import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { favorites } = useFavorites();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setMenuOpen(false);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? "text-primary-700 bg-primary-100/70"
        : "text-foreground-700 hover:text-primary-700 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-600 text-background-50 dark:text-foreground-950">
              <i className="ri-film-line text-xl"></i>
            </span>
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950 whitespace-nowrap">
              最近中文字幕
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-4">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto hidden md:flex items-center gap-3">
            <form onSubmit={submitSearch} className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                type="text"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、剧集、导演…"
                className="w-56 lg:w-64 rounded-full border border-background-300 bg-background-100 py-2 pl-9 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </form>
            <Link
              to="/favorites"
              className="relative w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-700 hover:text-accent-600 hover:border-accent-300 transition-colors"
              aria-label="我的收藏"
            >
              <i className="ri-bookmark-3-line text-lg"></i>
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-accent-500 text-[11px] font-semibold text-background-50 dark:text-foreground-950">
                  {favorites.length}
                </span>
              )}
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="ml-auto md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-800"
            aria-label="打开菜单"
          >
            <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* 移动端固定顶部搜索框 */}
        <form onSubmit={submitSearch} className="md:hidden relative pb-3">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
          <input
            type="text"
            name="q"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索影片、剧集、导演…"
            className="w-full rounded-full border border-background-300 bg-background-100 py-2.5 pl-9 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </form>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-lg text-base font-medium ${
                    isActive
                      ? "text-primary-700 bg-primary-100/70"
                      : "text-foreground-800 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/favorites"
              className="px-3 py-3 rounded-lg text-base font-medium text-foreground-800 hover:bg-background-100 flex items-center gap-2"
            >
              <i className="ri-bookmark-3-line"></i>
              我的收藏{favorites.length > 0 ? `（${favorites.length}）` : ""}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}